
import { useCallback, useEffect, useMemo } from 'react'

import { useAppDispatch, useAppSelector } from '@/shared/store'

import { dropdownsSelector } from '../dropdownsSelector'
import { upsertSingleDropdown } from '../dropdownsSlice'
import { dropdownSettings, DropdownSettingsKey } from '../models/DropdownSettings'
import { internalDropdowns, InternalDropdownsKey } from '../models/InternalDropdowns'
import { fetchDropdownData } from '../thunks/fetchDropdownData'

export type UseDropdownOptions = {
  forceRefresh?: boolean
  insertAll?: boolean
  insertBlank?: boolean
  sortBy?: 'label' | 'value'
}

export const useDropdown = (
  dropdownName: 'groups' | DropdownSettingsKey | InternalDropdownsKey,
  options: UseDropdownOptions = {},
) => {
  const dispatch = useAppDispatch()
  const dropdownState = useAppSelector(state => dropdownsSelector.selectById(state, dropdownName))
  const isLoading = useAppSelector(state => state.dropdowns.loading[dropdownName])
  const completed = useAppSelector(state => state.dropdowns.completed[dropdownName])
  const error = useAppSelector(state => state.dropdowns.error[dropdownName])

  const refreshDropdown = useCallback(() => {
    if (dropdownName in dropdownSettings) {
      dispatch(fetchDropdownData(dropdownName as DropdownSettingsKey))
    }
  }, [dispatch, dropdownName])

  useEffect(() => {
    // If it is an internal dropdown and it has not been setup, upsert the dropdown to redux store
    if (dropdownName in internalDropdowns && !dropdownState) {
      dispatch(upsertSingleDropdown({
        name: dropdownName,
        options: internalDropdowns[dropdownName as InternalDropdownsKey],
      }))
    }

    // If it has not been setup or forceRefresh is true, refresh the target dropdown
    else if (dropdownName in dropdownSettings && dropdownSettings[dropdownName as DropdownSettingsKey] && (!dropdownState || options.forceRefresh)) {
      refreshDropdown()
    }
  }, [dispatch, dropdownName, options.forceRefresh, dropdownState, refreshDropdown])

  // Process the data based on options
  const data = useMemo(() => {
    if (!dropdownState?.options || !completed) return []

    const result = [...dropdownState.options]

    if (options.sortBy) {
      result.sort((a, b) => {
        const aValue = a[options.sortBy!]
        const bValue = b[options.sortBy!]

        return String(aValue).localeCompare(String(bValue))
      })
    }

    if (options.insertBlank) {
      result.unshift({ label: '', value: '' })
    }

    if (options.insertAll) {
      result.unshift({ label: 'All', value: 0 })
    }

    return result
  }, [dropdownState?.options, options, completed])

  return {
    data,
    completed,
    isLoading,
    error,
    refreshDropdown,
  }
}